// Render del configure panel cuando el preset es 'design-modelo' (o
// 'design-auto', con `hideAutoPickers` para esconder los pickers que la
// IA decide sola). Muestra todas las opciones stackeadas: Modelo
// (inline desktop · botón mobile), Pose (carrusel inline desktop con
// "Ver más" · botón mobile), Fondo (inline desktop · botón mobile),
// Formato y "Otra config".
//
// El botón Generar NO vive acá — lo renderiza el ConfigurePanel padre
// al final, igual que en el resto de los kinds.

import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  Mountain,
  Sparkles,
  User,
  Wand2,
  X,
} from 'lucide-react';
import {
  ModelPickerInline,
  modelSelectionLabel,
  type ModelPickerView,
  type ModelSelection,
} from '../ModelPicker';
import { PosePickerInline } from '../PosePicker';
import {
  FondoPickerInline,
  fondoSelectionLabel,
  type FondoPickerView,
  type FondoSelection,
} from '../FondoPicker';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import type { MockProductV2 } from '../mockData';

// ─── AutoPlaceholder ──────────────────────────────────────────────────
// Visual para cuando Modelo o Pose están en modo "Automático" (sin
// selección). Muestra el ícono del tipo arriba + circulos de color
// (los del gradiente brand) con blur fuerte abajo, mezclados — da una
// vibe "auto/IA" sin la card sólida gradient que se sentía pesada.
const AutoPlaceholder: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
}> = ({ icon: Icon }) => {
  return (
    <div className="absolute inset-0">
      {/* Cluster de círculos blur — anclado al borde inferior, parte
          asomando hacia abajo. Cada círculo drift animado con valores
          de translate grandes para que el movimiento sea VISIBLE
          incluso con blur. Colores: azul, cyan, magenta (más
          contrastantes que los 3 azulados anteriores que se mezclaban
          en un solo tono al blurearse). */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] aspect-square translate-y-[50%]">
        <div
          className="absolute rounded-full blur-2xl animate-drift-1"
          style={{
            width: '60%',
            height: '60%',
            backgroundColor: '#1644EB',
            left: '5%',
            top: '20%',
            opacity: 0.95,
          }}
        />
        <div
          className="absolute rounded-full blur-2xl animate-drift-2"
          style={{
            width: '55%',
            height: '55%',
            backgroundColor: '#06B6D4',
            right: '5%',
            top: '15%',
            opacity: 0.85,
          }}
        />
        <div
          className="absolute rounded-full blur-2xl animate-drift-3"
          style={{
            width: '55%',
            height: '55%',
            backgroundColor: '#EC4899',
            left: '25%',
            bottom: '5%',
            opacity: 0.85,
          }}
        />
      </div>
      {/* Icono centrado en la card entera — independiente del cluster. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="w-10 h-10 text-neutral-400" />
      </div>
    </div>
  );
};

export interface DesignModeloPanelProps {
  variant: 'mobile' | 'desktop';
  /** Si true, esconde los pickers de Modelo / Pose / Fondo — usado en
   *  el modo 'design-auto' (Generar imágenes automáticamente) donde
   *  toda la escena la decide IA. */
  hideAutoPickers?: boolean;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — usado para renderizar el
   *  control +/- como card propia cuando `hideAutoPickers` es true. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  autoImageCountMax: number;
  products: MockProductV2[];
  onClearProducts: () => void;
  modelSelection: ModelSelection | null;
  onModelChange: (sel: ModelSelection) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  onOpenModelPicker: () => void;
  /** Abre el ModelPickerSheet directo a una vista (side panel desktop). */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi (slot pre-set). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  modelView: ModelPickerView;
  onModelViewChange: (v: ModelPickerView) => void;
  allPoses: PoseV2[];
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  onOpenPosePicker: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  /** Abre el FondoPickerSheet directo a una vista (describe / upload). */
  onOpenFondoSheet: (initialView: FondoPickerView) => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  formatoLabel: string;
  onOpenFormat: () => void;
  otraConfig: string;
  onOtraChange: (v: string) => void;
  /** Si true, oculta los botones de add/remove del multi-modelo
   *  (E-commerce: cantidad fija por la pose). */
  lockSlotCount?: boolean;
}

export const DesignModeloPanel: React.FC<DesignModeloPanelProps> = ({
  variant,
  hideAutoPickers = false,
  lockSlotCount = false,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  products,
  onClearProducts,
  modelSelection,
  onModelChange,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  modelView,
  onModelViewChange,
  allPoses,
  poseIds,
  onPoseIdsChange,
  onOpenPosePicker,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  onOpenFondoSheet,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  formatoLabel,
  onOpenFormat,
  otraConfig,
  onOtraChange,
}) => {
  const isDesktop = variant === 'desktop';
  const modelLabel = modelSelectionLabel(modelSelection);
  const fondoLabel = fondoSelectionLabel(fondoSelection);
  // Dropdown abierto del selector "Cantidad" del modo design-auto.
  // Cierra al elegir o al tap-fuera.
  const [autoCountOpen, setAutoCountOpen] = useState<boolean>(false);
  const autoCountRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!autoCountOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!autoCountRef.current?.contains(e.target as Node)) {
        setAutoCountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [autoCountOpen]);
  // Etiqueta amigable del valor actual ("1 imagen" / "N imágenes").
  const autoCountLabel = `${autoImageCount} ${
    autoImageCount === 1 ? 'imagen' : 'imágenes'
  }`;

  // Auto-resize del textarea "Instrucción adicional" en desktop. Crece
  // con cada nueva línea de texto sin mostrar el handle de resize en la
  // esquina. Se ajusta a `otraConfig` (cambia tras setear value o tras
  // limpiar). Reset a 'auto' antes de medir scrollHeight para que pueda
  // achicarse al borrar líneas.
  const otraTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const ta = otraTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    // Cap a 6 líneas exactas: computamos line-height + padding del
    // textarea para calcular la altura máxima en px (independiente
    // del viewport). Cuando el contenido supera, el textarea scrollea
    // internamente; cuando entra, sin scrollbar.
    const cs = window.getComputedStyle(ta);
    const lineH = parseFloat(cs.lineHeight) || 20;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;
    const maxH = lineH * 6 + padT + padB;
    const target = Math.min(ta.scrollHeight, maxH);
    ta.style.height = `${target}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, [otraConfig]);

  if (isDesktop) {
    // Cada sección es una card independiente con título (Playfair) +
    // descripción a la izquierda y el contenido del picker a la derecha.
    // Layout horizontal por card → fixed-width sidebar de título + área
    // de contenido flexible.
    //
    // En modo 'design-auto' (hideAutoPickers=true) el panel se
    // renderiza en el aside lateral derecho (~420-460px de ancho), que
    // es muy estrecho para el layout horizontal. Ahí usamos layout
    // VERTICAL — título y descripción arriba, contenido full-width
    // abajo — igual que el mobile.
    const sectionWrap = (
      title: string,
      description: string,
      content: React.ReactNode,
    ) =>
      hideAutoPickers ? (
        <section className="rounded-2xl bg-white border border-neutral-200 p-5 flex flex-col gap-3">
          <div>
            <h3 className="text-[17px] font-semibold text-brand-black/90 leading-tight">
              {title}
            </h3>
            <p className="text-[12.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
              {description}
            </p>
          </div>
          <div className="min-w-0">{content}</div>
        </section>
      ) : (
        <section className="rounded-2xl bg-white border border-neutral-200 p-6 flex gap-6">
          <div className="w-[220px] shrink-0">
            <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
              {title}
            </h3>
            <p className="text-[12.5px] text-[#3C3C43]/60 mt-1.5 leading-relaxed">
              {description}
            </p>
          </div>
          <div className="flex-1 min-w-0">{content}</div>
        </section>
      );
    return (
      <div className="flex flex-col gap-4">
        {sectionWrap(
          'Productos',
          'Vas a generar la imagen con estos productos.',
          <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
            {products.length === 1 ? (
              <img
                src={products[0].imageUrl}
                alt={products[0].name}
                className="w-12 h-12 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="flex shrink-0 -space-x-2">
                {products.slice(0, 3).map((p) => (
                  <img
                    key={p.id}
                    src={p.imageUrl}
                    alt={p.name}
                    className="w-10 h-10 rounded-lg object-cover ring-2 ring-[#F7F7F9]"
                  />
                ))}
                {products.length > 3 && (
                  <div className="w-10 h-10 rounded-lg bg-white ring-2 ring-[#F7F7F9] flex items-center justify-center text-[12px] font-semibold text-brand-black/70">
                    +{products.length - 3}
                  </div>
                )}
              </div>
            )}
            <p className="flex-1 min-w-0 text-[14.5px] font-semibold text-brand-black/90 truncate">
              {products.length === 1
                ? products[0].name
                : products.map((p) => p.name).join(', ')}
            </p>
            <button
              type="button"
              onClick={onClearProducts}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
              aria-label="Cambiar selección"
            >
              <X className="w-4 h-4" />
            </button>
          </div>,
        )}
        {!hideAutoPickers &&
          sectionWrap(
            'Modelo',
            'Quién va a aparecer en la imagen. Podés elegir uno guardado, crear uno nuevo o subir tus propias fotos.',
            <ModelPickerInline
              selection={modelSelection}
              onChange={onModelChange}
              view={modelView}
              onViewChange={onModelViewChange}
              userModels={userModels}
              userId={userAuthId}
              companyId={userCompanyId}
              onModelCreated={onModelCreated}
              hideLabel
              onOpenSheet={onOpenModelSheet}
              onOpenSheetForMultiSlot={onOpenModelSheetForMultiSlot}
              selectionsExtra={modelSelectionsExtra}
              onChangeExtras={onModelChangeExtras}
              onMultiEnabledChange={onMultiModelEnabledChange}
              multiProducts={products.map((p) => ({
                id: p.id,
                name: p.name,
                imageUrl: p.imageUrl,
              }))}
              multiProductsPerSlot={multiProductsPerSlot}
              onTapSlotProducts={onTapSlotProducts}
              lockSlotCount={lockSlotCount}
            />,
          )}
        {!hideAutoPickers &&
          sectionWrap(
            'Pose',
            'Elegí una o varias poses para tu modelo. Si seleccionás varias, generamos una imagen por cada una.',
            <PosePickerInline
              poses={allPoses}
              selectedIds={poseIds}
              onChange={onPoseIdsChange}
              onOpenSheet={onOpenPosePicker}
              hideLabel
              // En multi-modelo (>1 slot activo) la pose es selección
              // única: no soportamos generar variantes-por-pose con
              // varios modelos.
              singleSelect={
                multiModelEnabled && modelSelectionsExtra.length > 0
              }
            />,
          )}
        {!hideAutoPickers &&
          sectionWrap(
            'Fondo',
            'El escenario donde aparece el modelo. Elegí de la biblioteca, subí uno propio o describilo en palabras.',
            <FondoPickerInline
              selection={fondoSelection}
              onChange={onFondoSelectionChange}
              view={fondoView}
              onViewChange={onFondoViewChange}
              biblioteca={bibliotecaFondos}
              guardados={fondosGuardados}
              companyId={userCompanyId}
              onFondoSaved={onFondoSaved}
              hideLabel
              onOpenSheet={onOpenFondoSheet}
              selectedProductNames={products.map((p) => p.name)}
            />,
          )}
        {sectionWrap(
          hideAutoPickers ? 'Opciones' : 'Otras opciones',
          'Definí el formato de salida y agregá indicaciones extras para afinar el resultado.',
          <div className="flex flex-col gap-2.5">
            {/* Selector Cantidad — solo modo design-auto. Mismo
                container que el botón Formato (bg-white rounded-2xl,
                label izquierda + valor derecha) pero al tap despliega
                un dropdown absolute con los valores 1..N. */}
            {hideAutoPickers && (
              <div ref={autoCountRef} className="relative">
                <button
                  type="button"
                  onClick={() => setAutoCountOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={autoCountOpen}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="text-[13.5px] text-brand-black/85">
                    Cantidad
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-black/85">
                    {autoCountLabel}
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-[#3C3C43]/55 transition-transform ${
                        autoCountOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </span>
                </button>
                {autoCountOpen && (
                  <ul
                    role="listbox"
                    aria-label="Cantidad de imágenes"
                    className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden animate-mode-swap"
                  >
                    {Array.from(
                      { length: autoImageCountMax },
                      (_, i) => i + 1,
                    ).map((n) => {
                      const selected = n === autoImageCount;
                      const label = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
                      return (
                        <li key={n}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              onAutoImageCountChange(n);
                              setAutoCountOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-[13.5px] transition-colors flex items-center justify-between ${
                              selected
                                ? 'bg-brand-primary/[0.07] text-brand-primary font-semibold'
                                : 'text-brand-black/85 hover:bg-neutral-50'
                            }`}
                          >
                            <span>{label}</span>
                            {selected && (
                              <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onOpenFormat}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
            >
              <span className="text-[13.5px] text-brand-black/85">
                Formato
              </span>
              <span className="text-[13.5px] font-medium text-brand-black/85">
                {formatoLabel}
              </span>
            </button>
            <textarea
              ref={otraTextareaRef}
              value={otraConfig}
              onChange={(e) => onOtraChange(e.target.value)}
              placeholder="Instrucción adicional"
              rows={3}
              className="w-full resize-none overflow-hidden px-4 py-3 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
            />
          </div>,
        )}
      </div>
    );
  }

  // ── Mobile: cards cuadradas para Modelo+Pose (con thumbs visuales)
  //    + botones row Fondo/Formato + textarea Otra config. ─────────────
  // Resolver signed URL fresca para un slot 'preset' usando
  // `userModels` — la URL stored en la selección puede ser stale.
  const resolveFreshModelUrl = (sel: ModelSelection | null): string | null => {
    if (!sel || sel.mode !== 'preset') return null;
    const fresh = userModels.find((m) => String(m.id) === sel.id);
    return fresh?.thumbnailUrl || sel.imageUrl || null;
  };
  // Thumbnail del modelo seleccionado (preset) o gradiente fallback.
  const modelThumbUrl = resolveFreshModelUrl(modelSelection);
  // Poses seleccionadas resueltas a objetos completos (con imageUrl).
  const selectedPoses = poseIds
    .map((id) => allPoses.find((p) => p.id === id))
    .filter((p): p is PoseV2 => !!p);
  // Multi-modelo activo con al menos un slot extra: armamos un array
  // unificado [principal, ...extras] y mostramos el stack rotado
  // (mismo patrón visual que multi-pose). Cada slot reduce a su
  // `imageUrl` (preset → la url; auto/custom/null → null → gris).
  const isMultiModel = multiModelEnabled && modelSelectionsExtra.length > 0;
  const multiModelThumbs: { imageUrl: string | null }[] = isMultiModel
    ? [modelSelection, ...modelSelectionsExtra].map((s) => ({
        imageUrl: resolveFreshModelUrl(s),
      }))
    : [];

  return (
    <div className="flex flex-col gap-2.5">
      {/* Modelo + Pose en 2 columnas, cuadradas. */}
      {!hideAutoPickers && (
      <div className="grid grid-cols-2 gap-2.5">
        {/* MODELO — sin nombre debajo. Thumb 4:5 anclado arriba del card
            (debajo del label), su altura excede el espacio disponible y
            el `overflow-hidden` del card recorta la parte inferior, dando
            el efecto de "salir del borde inferior". */}
        <button
          type="button"
          onClick={onOpenModelPicker}
          className="relative aspect-square rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors overflow-hidden text-left min-w-0"
        >
          <span className="absolute top-3 left-3 z-10 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
            <User className="w-3 h-3" />
            Modelo
          </span>
          {isMultiModel ? (
            // Multi-modelo: stack rotado en abanico, mismo algoritmo
            // visual que el multi-pose (THUMB_W%, OFFSET_X%,
            // ROTATE_STEP°). Cada slot sin thumb (auto/custom/null)
            // cae al placeholder gris.
            <div className="absolute inset-x-3 top-9 aspect-[2/3]">
              {(() => {
                const slots = multiModelThumbs.slice(0, 4);
                const THUMB_W = 80;
                const OFFSET_X = 12;
                const ROTATE_STEP = 11;
                const totalSpreadX =
                  THUMB_W + Math.max(0, slots.length - 1) * OFFSET_X;
                const startX = Math.max(0, (100 - totalSpreadX) / 2);
                return (
                  <>
                    {slots.map((s, i) => {
                      const leftPct = startX + i * OFFSET_X;
                      const z = slots.length - i;
                      const rotation =
                        (i - (slots.length - 1) / 2) * ROTATE_STEP;
                      return (
                        <div
                          key={i}
                          // bg-neutral-200 sirve de placeholder cuando la
                          // signed URL caduca o falla — la `<img>` se
                          // oculta con onError y queda este fondo.
                          className="absolute top-0 aspect-[2/3] rounded-[16px] overflow-hidden ring-2 ring-white shadow-sm bg-neutral-200"
                          style={{
                            left: `${leftPct}%`,
                            width: `${THUMB_W}%`,
                            zIndex: z,
                            transform: `rotate(${rotation}deg)`,
                            transformOrigin: 'center top',
                          }}
                        >
                          {s.imageUrl && (
                            <img
                              src={s.imageUrl}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover object-top"
                              onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden';
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          ) : modelThumbUrl ? (
            <div className="absolute inset-x-3 top-9 aspect-[2/3] rounded-[20px] overflow-hidden ring-1 ring-black/[0.05] bg-neutral-200">
              <img
                src={modelThumbUrl}
                alt={modelLabel}
                className="absolute inset-0 w-full h-full object-cover object-top"
                onError={(e) => {
                  e.currentTarget.style.visibility = 'hidden';
                }}
              />
            </div>
          ) : (
            // Auto: el placeholder ocupa toda la card (no el wrapper
            // chico) para que ícono + círculos queden visualmente
            // centrados en el cuadrado completo.
            <AutoPlaceholder icon={Sparkles} />
          )}
        </button>

        {/* POSE — mismo patrón. Multi: stack offset desde la esquina. */}
        <button
          type="button"
          onClick={onOpenPosePicker}
          className="relative aspect-square rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors overflow-hidden text-left min-w-0"
        >
          <span className="absolute top-3 left-3 z-10 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
            <Wand2 className="w-3 h-3" />
            Pose
          </span>
          {selectedPoses.length === 0 ? (
            // Auto: placeholder centrado en toda la card (full inset).
            <AutoPlaceholder icon={Wand2} />
          ) : (
          <div
            className={`absolute inset-x-3 top-9 aspect-[2/3] ${
              selectedPoses.length === 1
                ? 'rounded-[20px] overflow-hidden ring-1 ring-black/[0.05]'
                : ''
            }`}
          >
            {selectedPoses.length === 1 ? (
              <img
                src={selectedPoses[0].imageUrl}
                alt={selectedPoses[0].nombre}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            ) : (
              // Stack acordeón vertical: hasta 3 thumbs reales + 1 slot
              // gris claro como "hay más" si selectedPoses > 3. Cada slot
              // se anchora arriba con offset Y incremental — su parte
              // inferior queda oculta detrás del próximo, creando la
              // sensación de cartas plegadas hacia abajo.
              (() => {
                const visible = selectedPoses.slice(0, 3);
                const showGray = selectedPoses.length > 3;
                type Slot = { kind: 'pose'; p: PoseV2 } | { kind: 'gray' };
                const slots: Slot[] = [
                  ...visible.map((p): Slot => ({ kind: 'pose', p })),
                  ...(showGray ? [{ kind: 'gray' } as Slot] : []),
                ];
                // Cartas en abanico horizontal, todas a la misma altura.
                // Z-index DECRECIENTE: la primera (izquierda) queda
                // arriba de la pila, cada siguiente (a la derecha) va
                // una capa más abajo — la izquierda overlapea sobre la
                // de la derecha. Width grande + aspect 2:3 hace que su
                // altura exceda el outer card, así su parte inferior
                // queda cortada por el `overflow-hidden` del card
                // (mismo efecto que la imagen del modelo solo).
                const THUMB_W = 80; // % del wrapper — alto para clip bottom
                const OFFSET_X = 12; // % horizontal por slot
                const ROTATE_STEP = 11; // grados por slot — fan marcado
                const totalSpreadX =
                  THUMB_W + Math.max(0, slots.length - 1) * OFFSET_X;
                const startX = Math.max(0, (100 - totalSpreadX) / 2);
                return (
                  <>
                    {slots.map((s, i) => {
                      const leftPct = startX + i * OFFSET_X;
                      // z decreciente: i=0 → mayor, i=N-1 → menor.
                      const z = slots.length - i;
                      const rotation =
                        (i - (slots.length - 1) / 2) * ROTATE_STEP;
                      const key =
                        s.kind === 'pose' ? `p-${s.p.id}` : `gray-${i}`;
                      return (
                        <div
                          key={key}
                          className="absolute top-0 aspect-[2/3] rounded-[16px] overflow-hidden ring-2 ring-white shadow-sm"
                          style={{
                            left: `${leftPct}%`,
                            width: `${THUMB_W}%`,
                            zIndex: z,
                            transform: `rotate(${rotation}deg)`,
                            transformOrigin: 'center top',
                          }}
                        >
                          {s.kind === 'pose' ? (
                            <img
                              src={s.p.imageUrl}
                              alt={s.p.nombre}
                              className="absolute inset-0 w-full h-full object-cover object-top"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-neutral-200" />
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            )}
          </div>
          )}
        </button>
      </div>
      )}

      {/* Fondo + Formato siguen como rows planas. */}
      {!hideAutoPickers && (
      <button
        type="button"
        onClick={onOpenFondoPicker}
        className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
      >
        <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
          <Mountain className="w-3 h-3" />
          Fondo
        </span>
        <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
          {fondoLabel}
        </span>
      </button>
      )}
      {/* Cantidad — card propia solo en modo 'design-auto', arriba del
          Formato. Layout horizontal: label izquierda · valor +
          chevron derecha. Tap abre un dropdown absolute con 1..N
          imágenes (mismo patrón que el desktop). */}
      {hideAutoPickers && (
        <div ref={autoCountRef} className="relative">
          <button
            type="button"
            onClick={() => setAutoCountOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={autoCountOpen}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
          >
            <span className="text-[14.5px] font-normal text-brand-black/90">
              Cantidad
            </span>
            <span className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold text-brand-black/90">
              {autoCountLabel}
              <ChevronDown
                className={`w-3.5 h-3.5 text-[#3C3C43]/55 transition-transform ${
                  autoCountOpen ? 'rotate-180' : ''
                }`}
              />
            </span>
          </button>
          {autoCountOpen && (
            <ul
              role="listbox"
              aria-label="Cantidad de imágenes"
              className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden animate-mode-swap"
            >
              {Array.from(
                { length: autoImageCountMax },
                (_, i) => i + 1,
              ).map((n) => {
                const selected = n === autoImageCount;
                const label = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
                return (
                  <li key={n}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onAutoImageCountChange(n);
                        setAutoCountOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-[14.5px] transition-colors flex items-center justify-between ${
                        selected
                          ? 'bg-brand-primary/[0.07] text-brand-primary font-semibold'
                          : 'text-brand-black/85 hover:bg-neutral-50'
                      }`}
                    >
                      <span>{label}</span>
                      {selected && (
                        <Check className="w-4 h-4" strokeWidth={3} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onOpenFormat}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
      >
        <span className="text-[14.5px] font-normal text-brand-black/90">
          Formato
        </span>
        <span className="text-[14.5px] font-semibold text-brand-black/90 truncate">
          {formatoLabel}
        </span>
      </button>
      {/* Textarea auto-grow: crece con las líneas hasta max-h-[45vh],
          después scrollea internamente. La altura se ajusta vía
          useEffect (otraTextareaRef + watchea otraConfig). */}
      <textarea
        ref={otraTextareaRef}
        value={otraConfig}
        onChange={(e) => onOtraChange(e.target.value)}
        placeholder="Instrucción adicional"
        rows={2}
        className="w-full px-4 py-3 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
      />
    </div>
  );
};
